'use client';

import { createColumnHelper } from '@tanstack/table-core';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { FiList, FiPlus } from 'react-icons/fi';
import { GrNodes } from 'react-icons/gr';
import { PiCaretCircleDownBold, PiCaretCircleUpBold } from 'react-icons/pi';
import { RiDatabaseLine } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useByoaAgentsQuery } from '@/hooks/queries/useByoaAgents';
import { useSecretsQuery } from '@/hooks/queries/useSecrets';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { SecretListItem, SecretTypeEnum } from '@/types/secret';
import { paramToSecretType, secretTypeToParam } from '@/utils/secretsLink';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import { ExpandedSecretRow } from './expandedSecretRow';
import { SecretActionMenu } from './secretActionMenu';
import { SecretModal } from './secretModal';
import styles from './secretsPanel.module.scss';


type TypeFilter = SecretTypeEnum | 'all';

const TYPE_FILTER_LABELS: Record<TypeFilter, string> = {
  all: 'All Types',
  [ SecretTypeEnum.AGENT ]: 'Constellation',
  [ SecretTypeEnum.EVALS ]: 'Evaluation',
  [ SecretTypeEnum.DATA_PLATFORM ]: 'Data Platform'
};

const TYPE_DISPLAY_LABELS: Record<SecretTypeEnum, string> = {
  [ SecretTypeEnum.AGENT ]: 'Constellation',
  [ SecretTypeEnum.EVALS ]: 'Evaluation',
  [ SecretTypeEnum.DATA_PLATFORM ]: 'Data Platform'
};

const columnHelper = createColumnHelper<SecretListItem>();

function getTypeLabel( secret: SecretListItem ): string {
  const base = TYPE_DISPLAY_LABELS[secret.type];

  if ( secret.type === SecretTypeEnum.AGENT || secret.type === SecretTypeEnum.EVALS ) {
    if ( secret.global ) {
      return `${ base } (all)`;
    }
    const count = secret.agentMappings?.length ?? 0;
    return `${ base } (${ count })`;
  }

  return base;
}

export function SecretsPanel() {
  const { data: secrets, isLoading } = useSecretsQuery();
  const { data: agents } = useByoaAgentsQuery();
  const [ useLocalZone ] = useLocalZonePreference();

  const agentNameMap = useMemo(
    () => new Map<string, string>(
      ( agents ?? [] ).flatMap( a => ( a.id ? [ [ a.id, a.name ?? a.id ] as const ] : [] ) )
    ),
    [ agents ]
  );

  const [ showCreateModal, setShowCreateModal ] = useState( false );
  const [ expandedRows, setExpandedRows ] = useState<Set<string>>( new Set() );

  const [ searchParams, setSearchParams ] = useSearchParams();
  const typeFilter: TypeFilter = paramToSecretType( searchParams.get( 'type' ) ) ?? 'all';
  const nameFilter = searchParams.get( 'name' ) ?? '';
  const agentFilter = searchParams.get( 'agent' ) ?? '';

  const setUrlParam = useCallback( ( key: string, value: string ) => {
    setSearchParams( ( prev ) => {
      const next = new URLSearchParams( prev );
      if ( value ) {
        next.set( key, value );
      } else {
        next.delete( key );
      }
      return next;
    }, { replace: true } );
  }, [ setSearchParams ] );

  const setTypeFilter = useCallback( ( value: TypeFilter ) => {
    setUrlParam( 'type', value === 'all' ? '' : secretTypeToParam( value ) );
  }, [ setUrlParam ] );

  const setNameFilter = useCallback( ( value: string ) => {
    setUrlParam( 'name', value );
  }, [ setUrlParam ] );

  const setAgentFilter = useCallback( ( value: string ) => {
    setUrlParam( 'agent', value );
  }, [ setUrlParam ] );

  const toggleRow = ( secretId: string ) => {
    setExpandedRows( prev => {
      const next = new Set( prev );
      if ( next.has( secretId ) ) {
        next.delete( secretId );
      } else {
        next.add( secretId );
      }
      return next;
    } );
  };

  const filteredSecrets = useMemo( () => {
    let result = secrets ?? [];

    if ( typeFilter !== 'all' ) {
      result = result.filter( s => s.type === typeFilter );
    }

    if ( nameFilter.trim() ) {
      const query = nameFilter.trim().toLowerCase();
      result = result.filter( s => s.name.toLowerCase().includes( query ) );
    }

    if ( agentFilter.trim() ) {
      const query = agentFilter.trim().toLowerCase();
      result = result.filter( s => s.agentMappings?.some( m => {
        const name = agentNameMap.get( m.agentId ) ?? m.agentId;
        return name.toLowerCase().includes( query );
      } )
      );
    }

    return result;
  }, [ secrets, typeFilter, nameFilter, agentFilter, agentNameMap ] );

  const columns = useMemo( () => [
    columnHelper.display( {
      id: 'type',
      header: 'Type',
      size: 250,
      cell: info => {
        const secret = info.row.original;
        const isExpanded = expandedRows.has( secret.id );
        const CaretIcon = isExpanded ? PiCaretCircleUpBold : PiCaretCircleDownBold;

        return (
          <span
            className={ styles.expand_toggle }
            onClick={ () => toggleRow( secret.id ) }
            role="button"
            tabIndex={ 0 }
            onKeyDown={ ( e ) => { if ( e.key === 'Enter' || e.key === ' ' ) toggleRow( secret.id ); } }
          >
            <Icon icon={ CaretIcon } size="sm" />
            { getTypeLabel( secret ) }
          </span>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 25 } )
      }
    } ),
    columnHelper.accessor( 'name', {
      header: 'Secret Name',
      size: 350,
      cell: info => <strong>{ info.getValue() }</strong>,
      meta: {
        classStr: buildColumnClassString( { width: 40, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.display( {
      id: 'lastUpdated',
      header: 'Last updated',
      size: 250,
      cell: info => {
        const value = info.row.original.updatedAt;
        if ( !value ) return '-';
        return (
          <TimeFormatter
            key={ info.row.original.id }
            sourceType={ TimeSourceTypeEnum.EPOCH_SECOND }
            value={ value }
            outputFormat={ OutputFormatEnum.STRING_SINGLE }
            opts={ { useLocalZone } }
          />
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.display( {
      id: 'actions',
      header: 'Actions',
      size: 100,
      cell: info => <SecretActionMenu secret={ info.row.original } />,
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.CENTER } ),
        centerHeader: true
      }
    } )
  ], [ expandedRows, useLocalZone ] );

  const table = useNativeTable<SecretListItem>( {
    data: filteredSecrets,
    columns,
    enableSorting: false
  } );

  const renderSubRow = ( secret: SecretListItem ): React.ReactNode | null => {
    if ( !expandedRows.has( secret.id ) ) return null;

    return <ExpandedSecretRow secret={ secret } agentNameMap={ agentNameMap } />;
  };

  if ( isLoading ) {
    return <Loader />;
  }

  return (
    <>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <Heading as="h2" size="lg">Managed Secrets</Heading>
        <Button
          variant='primary'
          icon={ <Icon icon={ FiPlus } /> }
          onClick={ () => setShowCreateModal( true ) }
        >
          Add Secret
        </Button>
      </div>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <Text size="md">Managed secrets are stored credentials with adjustable access controls.</Text>
      </div>

      <div className={ styles.filter_row }>
        <Menu
          variant="select"
          label="Filter by type"
          value={ TYPE_FILTER_LABELS[typeFilter] }
          data-testid="secrets-type-filter"
        >
          <MenuItem
            value="All Types"
            onClick={ () => setTypeFilter( 'all' ) }
            leadingIcon={ <Icon icon={ FiList } size="sm" /> }
          />
          <MenuItem
            value="Constellation"
            description="Deployed agentic systems"
            onClick={ () => setTypeFilter( SecretTypeEnum.AGENT ) }
            leadingIcon={ <Icon icon={ GrNodes } size="sm" /> }
          />
          <MenuItem
            value="Data Platform"
            description="Your data available inside KanpAI™"
            onClick={ () => setTypeFilter( SecretTypeEnum.DATA_PLATFORM ) }
            leadingIcon={ <Icon icon={ RiDatabaseLine } size="sm" /> }
          />
        </Menu>
        <div className={ styles.filter_right }>
          <Input
            type="text"
            label="Filter by secret name"
            placeholder="Search by secret name..."
            value={ nameFilter }
            onChange={ ( e ) => setNameFilter( e.target.value ) }
          />
          <Input
            type="text"
            label="Filter by constellation"
            placeholder="Filter by constellation..."
            value={ agentFilter }
            onChange={ ( e ) => setAgentFilter( e.target.value ) }
          />
        </div>
      </div>

      <NativeTable
        table={ table }
        pagination="client"
        tbodyTestId="table-body-secrets"
        renderSubRow={ renderSubRow }
        onRowClick={ ( secret ) => toggleRow( secret.id ) }
      />

      <SecretModal
        mode="create"
        isOpen={ showCreateModal }
        onClose={ () => setShowCreateModal( false ) }
      />
    </>
  );
}
